import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined"
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined"
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined"
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined"
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined"
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined"
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined"
import SendOutlinedIcon from "@mui/icons-material/SendOutlined"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined"
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined"
import VoicemailOutlinedIcon from "@mui/icons-material/VoicemailOutlined"

/*
 * Admin sidebar structure.
 *
 * Grouped by job-to-be-done. Items that are really one job split over two or
 * three existing pages carry `tabs`: the sidebar shows a single entry, and
 * the admin layout renders a tab strip that links between the sibling pages.
 * Every page keeps its original URL, so existing links and bookmarks work.
 */

export const DASHBOARD_NAV = {
  to: "/admin",
  label: "Dashboard",
  icon: DashboardOutlinedIcon,
  end: true,
}

export const AUDIT_LOGS_NAV = {
  to: "/admin/audit-logs",
  label: "Audit logs",
  icon: HistoryOutlinedIcon,
  tabs: [
    { to: "/admin/audit-logs", label: "Concierge security" },
    { to: "/admin/zoho-mailbox-audit", label: "Zoho mailbox" },
  ],
}

export const ADMIN_NAV_GROUPS = [
  {
    id: "accounts",
    label: "Accounts",
    icon: AccountCircleOutlinedIcon,
    defaultOpen: true,
    items: [
      {
        to: "/admin/users",
        label: "Users",
        icon: PeopleOutlinedIcon,
        tabs: [
          { to: "/admin/users", label: "All users" },
          { to: "/admin/trial-management", label: "Trials" },
        ],
      },
      {
        to: "/admin/mls-purchases",
        label: "MLS purchases",
        icon: ShoppingCartOutlinedIcon,
      },
    ],
  },
  {
    id: "meetings",
    label: "Meetings",
    icon: EventAvailableOutlinedIcon,
    items: [
      {
        to: "/admin/meetings",
        label: "Meetings",
        icon: EventAvailableOutlinedIcon,
        tabs: [
          { to: "/admin/meetings", label: "Calendar" },
          { to: "/admin/meetings/list", label: "List" },
          { to: "/admin/meetings/analytics", label: "Analytics" },
          { to: "/admin/meetings/configuration", label: "Configuration" },
        ],
      },
    ],
  },
  {
    id: "concierge-team",
    label: "Concierge team",
    icon: GroupsOutlinedIcon,
    items: [
      {
        to: "/admin/concierges",
        label: "Concierges",
        icon: GroupOutlinedIcon,
        tabs: [
          { to: "/admin/concierges", label: "Concierges" },
          { to: "/admin/assignments", label: "Assignments" },
        ],
      },
      {
        to: "/admin/pods",
        label: "Pods",
        icon: GroupsOutlinedIcon,
        feature: "pod_dialer",
      },
    ],
  },
  {
    id: "messaging",
    label: "Messaging",
    icon: ChatOutlinedIcon,
    items: [
      {
        to: "/admin/inbox-templates",
        label: "Templates",
        icon: DescriptionOutlinedIcon,
        tabs: [
          { to: "/admin/inbox-templates", label: "Email" },
          { to: "/admin/inbox-sms-templates", label: "SMS" },
        ],
      },
      {
        to: "/admin/inbound-voicemail",
        label: "Voicemail",
        icon: VoicemailOutlinedIcon,
      },
      {
        to: "/admin/activity-report-emails",
        label: "Activity reports",
        icon: SendOutlinedIcon,
      },
    ],
  },
  {
    id: "setup",
    label: "Phone & email",
    icon: SettingsOutlinedIcon,
    items: [
      {
        to: "/admin/concierge-calling-numbers",
        label: "Twilio numbers",
        icon: PhoneInTalkOutlinedIcon,
      },
      {
        to: "/admin/zoho-mailbox",
        label: "Zoho mail",
        icon: EmailOutlinedIcon,
        tabs: [
          { to: "/admin/zoho-mailbox", label: "Mailboxes" },
          { to: "/admin/zoho-mail-org", label: "Org connection" },
        ],
      },
    ],
  },
  {
    id: "security",
    label: "Security",
    icon: ShieldOutlinedIcon,
    items: [AUDIT_LOGS_NAV],
  },
]

export const SYSTEM_STATUS_NAV = {
  to: "/admin/system-status",
  label: "System status",
  icon: MonitorHeartOutlinedIcon,
}

/* Pinned below the groups, with logout. */
export const ADMIN_NAV_FOOTER = [SYSTEM_STATUS_NAV]

export function isNavItemActive(pathname, to, end = false) {
  if (end) {
    return pathname === to || pathname === `${to}/`
  }
  return pathname === to || pathname.startsWith(`${to}/`)
}

/* An item is active on its own page or on any of its sibling tab pages. */
export function isNavEntryActive(pathname, item) {
  if (item.tabs?.length) {
    return item.tabs.some((tab) => isNavItemActive(pathname, tab.to))
  }
  return isNavItemActive(pathname, item.to, item.end)
}

export function isNavGroupActive(pathname, group) {
  return group.items.some((item) => isNavEntryActive(pathname, item))
}

export function findActiveNavGroupId(pathname) {
  const group = ADMIN_NAV_GROUPS.find((g) => isNavGroupActive(pathname, g))
  return group ? group.id : null
}

/* The nav entry whose sibling tabs should render above the current page. */
export function findActiveTabbedNavItem(pathname) {
  const entries = [
    ...ADMIN_NAV_GROUPS.flatMap((group) => group.items),
    ...ADMIN_NAV_FOOTER,
  ]
  return (
    entries.find(
      (item) => item.tabs?.length && isNavEntryActive(pathname, item)
    ) || null
  )
}

/* Every page path reachable from the sidebar (entries plus their tabs). */
export function allNavPaths() {
  const entries = [
    DASHBOARD_NAV,
    ...ADMIN_NAV_GROUPS.flatMap((group) => group.items),
    ...ADMIN_NAV_FOOTER,
  ]
  const paths = entries.flatMap((item) =>
    item.tabs?.length ? item.tabs.map((tab) => tab.to) : [item.to]
  )
  return Array.from(new Set(paths))
}

export const ADMIN_NAV_OPEN_GROUPS_STORAGE_KEY =
  "smartsetter.admin.sidebar.openGroups"

/* Groups open on a first visit (before the browser has saved any state). */
export function defaultOpenGroups() {
  return Object.fromEntries(
    ADMIN_NAV_GROUPS.filter((group) => group.defaultOpen).map((group) => [
      group.id,
      true,
    ])
  )
}

export function readStoredOpenGroups() {
  try {
    const raw = window.localStorage.getItem(ADMIN_NAV_OPEN_GROUPS_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {}
  } catch (error) {
    return {}
  }
}

export function writeStoredOpenGroups(openGroups) {
  try {
    window.localStorage.setItem(
      ADMIN_NAV_OPEN_GROUPS_STORAGE_KEY,
      JSON.stringify(openGroups)
    )
  } catch (error) {
    // Private mode / blocked storage: the sidebar still works, just unsaved.
  }
}

/** Drop nav items whose feature switch is off (item.feature names the switch). */
export function navGroupsForFeatures(groups, features) {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !item.feature || Boolean(features?.[item.feature])
      ),
    }))
    .filter((group) => group.items.length > 0)
}
